'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

// All geometry is stored NORMALIZED to the image box (0..1): x/cx/x1 are
// fractions of width, y/cy/y1 fractions of height, rx a fraction of width, ry a
// fraction of height, strokeWidth a fraction of width, fontSize a fraction of
// height. That keeps markup resolution-independent so it renders identically in
// the editor, the thumbnail, and the Phase-3 PDF.
export interface Annotation {
  id: string;
  type: 'arrow' | 'circle' | 'text';
  color: string;
  strokeWidth?: number;
  // arrow
  x1?: number; y1?: number; x2?: number; y2?: number;
  // circle (ellipse)
  cx?: number; cy?: number; rx?: number; ry?: number;
  // text
  x?: number; y?: number; text?: string; fontSize?: number;
}

interface PhotoAnnotatorProps {
  src: string;
  initial: Annotation[];
  onCancel: () => void;
  onSave: (anns: Annotation[]) => void | Promise<void>;
}

const COLORS = ['#EF4444', '#F59E0B', '#FACC15', '#22C55E', '#3B82F6', '#FFFFFF', '#111827'];
const DEF_SW = 0.006;
const DEF_FS = 0.05;
const HIT_PX = 12;

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

export default function PhotoAnnotator({ src, initial, onCancel, onSave }: PhotoAnnotatorProps) {
  const [tool, setTool] = useState<'select' | 'arrow' | 'circle' | 'text'>('select');
  const [color, setColor] = useState('#EF4444');
  const [anns, setAnns] = useState<Annotation[]>(initial || []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [saving, setSaving] = useState(false);

  const svgRef = useRef<SVGSVGElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<any>(null);
  const historyRef = useRef<Annotation[][]>([]);

  // Measure the rendered image so we can convert normalized <-> pixels.
  useEffect(() => {
    const measure = () => {
      const el = imgRef.current;
      if (el) setDims({ w: el.clientWidth, h: el.clientHeight });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const selected = anns.find(a => a.id === selectedId) || null;

  const pushHistory = (snapshot: Annotation[]) => {
    historyRef.current.push(snapshot);
    if (historyRef.current.length > 50) historyRef.current.shift();
  };
  const undo = () => {
    const prev = historyRef.current.pop();
    if (prev) { setAnns(prev); setSelectedId(null); setEditingId(null); }
  };

  const update = (id: string, patch: Partial<Annotation>) =>
    setAnns(prev => prev.map(a => (a.id === id ? { ...a, ...patch } : a)));

  const toNorm = (clientX: number, clientY: number) => {
    const r = svgRef.current!.getBoundingClientRect();
    return { nx: clamp01((clientX - r.left) / r.width), ny: clamp01((clientY - r.top) / r.height) };
  };

  // ---- hit testing (in pixels) ----
  const P = (nx: number, ny: number) => ({ x: nx * dims.w, y: ny * dims.h });
  const distToSeg = (px: number, py: number, ax: number, ay: number, bx: number, by: number) => {
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy || 1;
    let t = ((px - ax) * dx + (py - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    return Math.hypot(px - cx, py - cy);
  };

  const hitHandle = (a: Annotation, nx: number, ny: number): string | null => {
    const pt = P(nx, ny);
    const near = (hx: number, hy: number) => Math.hypot(pt.x - hx, pt.y - hy) <= HIT_PX + 4;
    if (a.type === 'arrow') {
      const p1 = P(a.x1!, a.y1!), p2 = P(a.x2!, a.y2!);
      if (near(p1.x, p1.y)) return 'p1';
      if (near(p2.x, p2.y)) return 'p2';
    } else if (a.type === 'circle') {
      const corners: [number, number][] = [
        [a.cx! - a.rx!, a.cy! - a.ry!], [a.cx! + a.rx!, a.cy! - a.ry!],
        [a.cx! - a.rx!, a.cy! + a.ry!], [a.cx! + a.rx!, a.cy! + a.ry!],
      ];
      for (const [cxn, cyn] of corners) { const c = P(cxn, cyn); if (near(c.x, c.y)) return 'corner'; }
    }
    return null;
  };

  const hitShape = (nx: number, ny: number): string | null => {
    const pt = P(nx, ny);
    for (let i = anns.length - 1; i >= 0; i--) {
      const a = anns[i];
      if (a.type === 'arrow') {
        const p1 = P(a.x1!, a.y1!), p2 = P(a.x2!, a.y2!);
        if (distToSeg(pt.x, pt.y, p1.x, p1.y, p2.x, p2.y) <= HIT_PX) return a.id;
      } else if (a.type === 'circle') {
        const nxr = (nx - a.cx!) / (a.rx! || 0.001), nyr = (ny - a.cy!) / (a.ry! || 0.001);
        if (nxr * nxr + nyr * nyr <= 1.25) return a.id;
      } else if (a.type === 'text') {
        const o = P(a.x!, a.y!);
        const fpx = (a.fontSize ?? DEF_FS) * dims.h;
        const wpx = Math.max(fpx, (a.text || '').length * fpx * 0.55);
        if (pt.x >= o.x - 4 && pt.x <= o.x + wpx + 4 && pt.y >= o.y - fpx && pt.y <= o.y + fpx * 0.35) return a.id;
      }
    }
    return null;
  };

  // ---- pointer flow ----
  const onPointerDown = (e: React.PointerEvent) => {
    if (editingId) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const { nx, ny } = toNorm(e.clientX, e.clientY);

    // resize handle of the selected shape
    if (selected) {
      const h = hitHandle(selected, nx, ny);
      if (h) { pushHistory(anns); dragRef.current = { action: 'resize', id: selected.id, handle: h }; return; }
    }

    if (tool === 'text') {
      pushHistory(anns);
      const id = uid();
      setAnns(prev => [...prev, { id, type: 'text', x: nx, y: ny, text: 'Text', color, fontSize: DEF_FS }]);
      setSelectedId(id); setEditingId(id); setTool('select');
      return;
    }

    const hitId = hitShape(nx, ny);
    if (tool === 'select') {
      if (hitId) { pushHistory(anns); setSelectedId(hitId); dragRef.current = { action: 'move', id: hitId, start: { nx, ny }, orig: anns.find(a => a.id === hitId) }; }
      else setSelectedId(null);
      return;
    }

    // create arrow / circle
    pushHistory(anns);
    const id = uid();
    if (tool === 'arrow') {
      setAnns(prev => [...prev, { id, type: 'arrow', x1: nx, y1: ny, x2: nx, y2: ny, color, strokeWidth: DEF_SW }]);
      dragRef.current = { action: 'create-arrow', id };
    } else {
      setAnns(prev => [...prev, { id, type: 'circle', cx: nx, cy: ny, rx: 0, ry: 0, color, strokeWidth: DEF_SW }]);
      dragRef.current = { action: 'create-circle', id, anchor: { nx, ny } };
    }
    setSelectedId(id);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const { nx, ny } = toNorm(e.clientX, e.clientY);
    if (d.action === 'create-arrow') update(d.id, { x2: nx, y2: ny });
    else if (d.action === 'create-circle') {
      update(d.id, { cx: (d.anchor.nx + nx) / 2, cy: (d.anchor.ny + ny) / 2, rx: Math.abs(nx - d.anchor.nx) / 2, ry: Math.abs(ny - d.anchor.ny) / 2 });
    } else if (d.action === 'move') {
      const dx = nx - d.start.nx, dy = ny - d.start.ny, o = d.orig as Annotation;
      if (o.type === 'arrow') update(d.id, { x1: clamp01(o.x1! + dx), y1: clamp01(o.y1! + dy), x2: clamp01(o.x2! + dx), y2: clamp01(o.y2! + dy) });
      else if (o.type === 'circle') update(d.id, { cx: clamp01(o.cx! + dx), cy: clamp01(o.cy! + dy) });
      else update(d.id, { x: clamp01(o.x! + dx), y: clamp01(o.y! + dy) });
    } else if (d.action === 'resize') {
      const a = anns.find(x => x.id === d.id);
      if (!a) return;
      if (a.type === 'arrow') { if (d.handle === 'p1') update(d.id, { x1: nx, y1: ny }); else update(d.id, { x2: nx, y2: ny }); }
      else if (a.type === 'circle') update(d.id, { rx: Math.abs(nx - a.cx!), ry: Math.abs(ny - a.cy!) });
    }
  };

  const onPointerUp = () => {
    const d = dragRef.current;
    if (d && (d.action === 'create-arrow' || d.action === 'create-circle')) {
      const a = anns.find(x => x.id === d.id);
      if (a) {
        const tiny = a.type === 'arrow'
          ? Math.hypot((a.x2! - a.x1!) * dims.w, (a.y2! - a.y1!) * dims.h) < 8
          : (a.rx! * dims.w < 5 && a.ry! * dims.h < 5);
        if (tiny) { setAnns(prev => prev.filter(x => x.id !== d.id)); setSelectedId(null); }
      }
    }
    dragRef.current = null;
  };

  // keyboard: delete selected
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editingId) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        pushHistory(anns);
        setAnns(prev => prev.filter(a => a.id !== selectedId));
        setSelectedId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, editingId, anns]);

  const applyColor = (c: string) => { setColor(c); if (selected) update(selected.id, { color: c }); };
  const bumpSize = (dir: 1 | -1) => {
    if (!selected) return;
    if (selected.type === 'text') update(selected.id, { fontSize: Math.max(0.02, Math.min(0.2, (selected.fontSize ?? DEF_FS) + dir * 0.008)) });
    else update(selected.id, { strokeWidth: Math.max(0.002, Math.min(0.02, (selected.strokeWidth ?? DEF_SW) + dir * 0.0015)) });
  };
  const deleteSelected = () => { if (!selected) return; pushHistory(anns); setAnns(prev => prev.filter(a => a.id !== selected.id)); setSelectedId(null); };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(anns); } finally { setSaving(false); }
  };

  // ---- render helpers ----
  const arrowHead = (a: Annotation) => {
    const p1 = P(a.x1!, a.y1!), p2 = P(a.x2!, a.y2!);
    const ang = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    const sw = (a.strokeWidth ?? DEF_SW) * dims.w;
    const len = Math.max(12, sw * 4.5);
    const b1 = { x: p2.x - len * Math.cos(ang - Math.PI / 7), y: p2.y - len * Math.sin(ang - Math.PI / 7) };
    const b2 = { x: p2.x - len * Math.cos(ang + Math.PI / 7), y: p2.y - len * Math.sin(ang + Math.PI / 7) };
    return `${p2.x},${p2.y} ${b1.x},${b1.y} ${b2.x},${b2.y}`;
  };

  const cursor = tool === 'select' ? 'default' : 'crosshair';

  return (
    <div className="fixed inset-0 z-[150] bg-black/85 flex flex-col">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 md:px-5 py-2.5 bg-[#1C2E5E] overflow-x-auto">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {([
            { t: 'select', icon: 'CursorArrowRaysIcon', lab: 'Select' },
            { t: 'arrow', icon: 'ArrowUpRightIcon', lab: 'Arrow' },
            { t: 'circle', icon: 'StopIcon', lab: 'Circle' },
            { t: 'text', icon: 'ChatBubbleBottomCenterTextIcon', lab: 'Text' },
          ] as const).map(b => (
            <button
              key={b.t}
              onClick={() => { setTool(b.t); setEditingId(null); }}
              title={b.lab}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${tool === b.t ? 'bg-white text-[#1C2E5E]' : 'text-white/70 hover:text-white'}`}
            >
              <Icon name={b.icon as any} size={15} />
              <span className="hidden sm:inline">{b.lab}</span>
            </button>
          ))}
          <div className="w-px h-6 bg-white/20 mx-1" />
          {/* Colors */}
          <div className="flex items-center gap-1">
            {COLORS.map(c => (
              <button
                key={c}
                onClick={() => applyColor(c)}
                title={c}
                className={`w-5 h-5 rounded-full border-2 ${color === c ? 'border-[#E89A3C]' : 'border-white/30'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="w-px h-6 bg-white/20 mx-1" />
          <button onClick={() => bumpSize(-1)} disabled={!selected} title="Smaller" className="px-2 py-1.5 rounded-md text-white/80 hover:text-white disabled:opacity-30"><Icon name="MinusIcon" size={14} /></button>
          <button onClick={() => bumpSize(1)} disabled={!selected} title="Bigger" className="px-2 py-1.5 rounded-md text-white/80 hover:text-white disabled:opacity-30"><Icon name="PlusIcon" size={14} /></button>
          <button onClick={deleteSelected} disabled={!selected} title="Delete" className="px-2 py-1.5 rounded-md text-white/80 hover:text-red-300 disabled:opacity-30"><Icon name="TrashIcon" size={14} /></button>
          <button onClick={undo} title="Undo" className="px-2 py-1.5 rounded-md text-white/80 hover:text-white"><Icon name="ArrowUturnLeftIcon" size={14} /></button>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm font-semibold text-white/80 hover:text-white">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: '#E89A3C' }}>
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="CheckIcon" size={15} variant="solid" />}
            Save
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-4">
        <div className="relative inline-block" style={{ touchAction: 'none' }}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            ref={imgRef}
            src={src}
            alt="Annotate"
            draggable={false}
            onLoad={() => { const el = imgRef.current; if (el) setDims({ w: el.clientWidth, h: el.clientHeight }); }}
            className="block max-w-full max-h-[76vh] select-none rounded"
          />
          <svg
            ref={svgRef}
            className="absolute inset-0"
            width={dims.w}
            height={dims.h}
            style={{ cursor, touchAction: 'none' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onDoubleClick={e => {
              const { nx, ny } = toNorm(e.clientX, e.clientY);
              const id = hitShape(nx, ny);
              const a = anns.find(x => x.id === id);
              if (a?.type === 'text') { setSelectedId(id!); setEditingId(id!); }
            }}
          >
            {anns.map(a => {
              const sel = a.id === selectedId;
              const swpx = (a.strokeWidth ?? DEF_SW) * dims.w;
              if (a.type === 'arrow') {
                const p1 = P(a.x1!, a.y1!), p2 = P(a.x2!, a.y2!);
                return (
                  <g key={a.id}>
                    <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={a.color} strokeWidth={swpx} strokeLinecap="round" />
                    <polygon points={arrowHead(a)} fill={a.color} />
                    {sel && <>
                      <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#E89A3C" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
                      <circle cx={p1.x} cy={p1.y} r={6} fill="#fff" stroke="#E89A3C" strokeWidth={2} />
                      <circle cx={p2.x} cy={p2.y} r={6} fill="#fff" stroke="#E89A3C" strokeWidth={2} />
                    </>}
                  </g>
                );
              }
              if (a.type === 'circle') {
                const c = P(a.cx!, a.cy!);
                const rxp = a.rx! * dims.w, ryp = a.ry! * dims.h;
                return (
                  <g key={a.id}>
                    <ellipse cx={c.x} cy={c.y} rx={rxp} ry={ryp} fill="none" stroke={a.color} strokeWidth={swpx} />
                    {sel && [[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy], i) => (
                      <circle key={i} cx={c.x + sx * rxp} cy={c.y + sy * ryp} r={6} fill="#fff" stroke="#E89A3C" strokeWidth={2} />
                    ))}
                  </g>
                );
              }
              // text
              const o = P(a.x!, a.y!);
              const fpx = (a.fontSize ?? DEF_FS) * dims.h;
              return (
                <g key={a.id}>
                  <text x={o.x} y={o.y} fill={a.color} fontSize={fpx} fontWeight={700} style={{ paintOrder: 'stroke', stroke: a.color === '#FFFFFF' ? '#111827' : 'rgba(0,0,0,0.35)', strokeWidth: Math.max(1, fpx * 0.06) }}>
                    {a.text || ' '}
                  </text>
                  {sel && <rect x={o.x - 4} y={o.y - fpx} width={Math.max(fpx, (a.text || '').length * fpx * 0.55) + 8} height={fpx * 1.3} fill="none" stroke="#E89A3C" strokeWidth={1} strokeDasharray="4 3" />}
                </g>
              );
            })}
          </svg>

          {/* Inline text editor */}
          {editingId && (() => {
            const a = anns.find(x => x.id === editingId);
            if (!a) return null;
            const o = P(a.x!, a.y!);
            const fpx = (a.fontSize ?? DEF_FS) * dims.h;
            return (
              <input
                autoFocus
                value={a.text || ''}
                onChange={e => update(editingId, { text: e.target.value })}
                onBlur={() => setEditingId(null)}
                onKeyDown={e => { if (e.key === 'Enter') setEditingId(null); }}
                className="absolute bg-white/95 border border-[#E89A3C] rounded px-1 outline-none"
                style={{ left: o.x, top: o.y - fpx, fontSize: fpx, fontWeight: 700, color: a.color, minWidth: 60 }}
              />
            );
          })()}
        </div>
      </div>

      {/* Hint */}
      <div className="flex-shrink-0 px-4 py-1.5 text-center text-xs text-white/50 bg-black/40">
        {tool === 'select' ? 'Click a mark to select · drag to move · drag the dots to resize · double-tap text to edit'
          : tool === 'text' ? 'Tap the photo to place a text label'
          : `Drag on the photo to draw ${tool === 'arrow' ? 'an arrow' : 'a circle'}`}
      </div>
    </div>
  );
}
